import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Sparkles, CalendarClock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PLANS, higherTiers } from "@/lib/plans";

// Listens for `evermin:daily-limit` events (fired by api.js / streamChat) and
// shows a tiered upgrade popup. Free→Basic/Plus/Pro, Basic→Plus/Pro, Plus→Pro,
// Pro→"limit reached". Mounted once at the app root.
export function UpgradeLimitModal() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [tier, setTier] = useState(null);

  useEffect(() => {
    const handler = (e) => setTier(e?.detail?.tier || "free");
    window.addEventListener("evermin:daily-limit", handler);
    return () => window.removeEventListener("evermin:daily-limit", handler);
  }, []);

  const open = tier != null;
  const options = tier ? higherTiers(tier) : [];
  const isPro = tier === "pro";
  const reached = PLANS[tier] || PLANS.free;

  const go = (target) => {
    setTier(null);
    navigate(`/membership?upgrade=${target}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && setTier(null)}>
      <DialogContent data-testid="limit-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-xl">
            {isPro ? <CalendarClock className="h-5 w-5 text-slate-500" /> : <Crown className="h-5 w-5 text-amber-500" />}
            {isPro ? t("limit_reached_title") : t("limit_title")}
          </DialogTitle>
          <DialogDescription data-testid="limit-modal-message">
            {isPro
              ? t("limit_pro_desc")
              : t("limit_desc").replace("{plan}", reached.name[language] || reached.name.en).replace("{count}", reached.ai)}
          </DialogDescription>
        </DialogHeader>

        {!isPro && (
          <div className="mt-1 space-y-2.5" data-testid="limit-modal-options">
            {options.map((id, idx) => {
              const p = PLANS[id];
              const featured = idx === 0;
              return (
                <button
                  key={id}
                  data-testid={`limit-upgrade-${id}`}
                  onClick={() => go(id)}
                  className={`flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-colors ${
                    featured
                      ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div>
                    <p className="font-display font-semibold text-slate-900">
                      EVERMIN {p.name[language] || p.name.en}
                    </p>
                    <p className="text-sm text-slate-500">
                      {p.ai} {t("ai_per_day_label")} · {p.tagline[language] || p.tagline.en}
                    </p>
                  </div>
                  <span className="ml-3 flex-shrink-0 font-display text-lg font-bold text-slate-900">
                    ₹{p.price}
                    <span className="text-xs font-medium text-slate-400">{t("per_month")}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-2 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => setTier(null)} data-testid="limit-modal-close">
            {t("close")}
          </Button>
          {!isPro && (
            <Button
              onClick={() => go(options[0])}
              data-testid="limit-modal-cta"
              className="gap-1.5 rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800"
            >
              <Sparkles className="h-4 w-4" /> {t("see_plans")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
